import React from "react";

import { clamp } from "@kazoohr/helpers";

import Header from "../Header";
import { Text } from "../Text";

import styles from "./Progress.scss";

export interface ProgressProps {
  /**
   * Should progress indicator be a circle
   */
  circular?: boolean;
  /**
   * Caps the maximum percentage value
   */
  maxPercent?: number;
  /**
   * Caps the minimum percentage value
   */
  minPercent?: number;
  /**
   * Percentage value to display
   */
  percent: number;
  /**
   * Whether the circular progress takes the width of it's container
   */
  fullWidth?: boolean;
  /**
   * Displays helper text under the Progress circle
   */
  helper?: string;
}

/**
 * A static Progress indicator component. Configurable to cap percent values so there is always a little bit of progress.
 */
export function Progress({
  maxPercent = 1,
  minPercent = 0,
  percent,
  circular,
  helper,
  fullWidth = false,
}: ProgressProps) {
  const clampedPercent = clamp(minPercent, maxPercent, percent);
  const largePercent = clampedPercent * 100;

  return (
    <div
      className={styles.progress}
      role="progressbar"
      aria-valuenow={largePercent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {circular ? (
        <div style={{ width: fullWidth ? "auto" : "94px", minHeight: "106px" }}>
          <Circle helper={helper} percent={clampedPercent} />
        </div>
      ) : (
        <div className={styles.progress__bar}>
          <div
            style={{ width: `${largePercent}%`, maxWidth: "100%" }}
            className={styles.progress__bar_filler}
          />
        </div>
      )}
    </div>
  );
}

interface CircleProps {
  percent: number;
  helper?: string;
}

function Circle({ percent, helper }: CircleProps) {
  const radius = 23;
  const circumference = 2 * Math.PI * radius;
  const percentageValue = circumference * (1 - percent);

  const circleProps = {
    cx: 50,
    cy: 50,
    fill: "none",
    r: radius,
    strokeWidth: "4",
  };

  return (
    <div className={styles.progress__container}>
      <svg
        className={styles.progress__circular}
        viewBox="25 25 50 50"
        strokeLinecap="round"
      >
        <circle className={styles.progress__circular__path} {...circleProps} />
        <circle
          className={styles.progress__circular__path__value}
          {...circleProps}
          strokeDasharray={circumference}
          strokeDashoffset={percentageValue}
        />
      </svg>
      <Header size="h5" className={styles.progress__percentage} level="5">
        {(percent * 100).toFixed(0) + "%"}
      </Header>
      <div className={styles.progress__text}>
        <Text size="paragraphXsmall" color="#666">
          {helper}
        </Text>
      </div>
    </div>
  );
}
